import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Page,
  Card,
  TextField,
  BlockStack,
  Text,
  Banner,
  Checkbox,
  Box,
} from "@shopify/polaris";
import { SaveBar } from "@shopify/app-bridge-react";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import { fetchShopSettings, saveShopSettings } from "../../api/shopSettingsApi";
import { showAppToast } from "../../utils/showAppToast";
import { FormPageSkeleton } from "../../components/PageSkeleton";

const SAVE_BAR_ID = "fsb-shop-settings-save-bar";

const DEFAULT_THEME = {
  cart_drawer_selector: "",
  cart_drawer_open_selectors: "",
  cart_section_ids: "",
  cart_drawer_inner_selector: "",
  cart_bubble_selector: "",
  open_drawer_after_add: true,
};

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function settingsToJson(settings) {
  return JSON.stringify(settings);
}

export default function AppSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [syncWarning, setSyncWarning] = useState(null);
  const [customCss, setCustomCss] = useState("");
  const [customJs, setCustomJs] = useState("");
  const [theme, setTheme] = useState({ ...DEFAULT_THEME });
  const savedRef = useRef(null);

  useAppTitleBar({ title: "Settings" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchShopSettings();
        if (cancelled) return;
        const loaded = {
          customCss: data?.customCss || "",
          customJs: data?.customJs || "",
          theme: { ...DEFAULT_THEME, ...(data?.theme || {}) },
        };
        setCustomCss(loaded.customCss);
        setCustomJs(loaded.customJs);
        setTheme(loaded.theme);
        savedRef.current = deepClone(loaded);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const snapshot = useMemo(
    () => ({ customCss, customJs, theme: deepClone(theme) }),
    [customCss, customJs, theme],
  );

  const isDirty =
    savedRef.current != null && settingsToJson(snapshot) !== settingsToJson(savedRef.current);

  const patchTheme = useCallback((patch) => {
    setTheme((prev) => ({ ...prev, ...patch }));
  }, []);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSyncWarning(null);
    try {
      const result = await saveShopSettings(snapshot);
      savedRef.current = deepClone(snapshot);
      const sync = result?.metafieldSync;
      if (sync && !sync.synced) {
        const detail = sync.error || "Unknown error";
        setSyncWarning(`Saved in app, but storefront sync failed: ${detail}`);
        showAppToast("Settings saved, but storefront sync failed", { isError: true });
      } else {
        showAppToast("Settings saved");
      }
    } catch (e) {
      setError(e?.message || "Save failed");
      showAppToast(e?.message || "Save failed", { isError: true });
    } finally {
      setSaving(false);
    }
  }, [snapshot]);

  const onDiscard = useCallback(() => {
    const saved = savedRef.current;
    if (!saved) return;
    setCustomCss(saved.customCss);
    setCustomJs(saved.customJs);
    setTheme(deepClone(saved.theme));
  }, []);

  if (loading) {
    return <FormPageSkeleton sections={3} />;
  }

  return (
    <Page>
      <SaveBar id={SAVE_BAR_ID} open={isDirty}>
        <button variant="primary" onClick={() => void onSave()} loading={saving ? "" : undefined}>
          Save
        </button>
        <button onClick={onDiscard}>Discard</button>
      </SaveBar>

      <BlockStack gap="400">
        {error ? (
          <Banner tone="critical" title="Could not load settings">
            <p>{error}</p>
          </Banner>
        ) : null}
        {syncWarning ? (
          <Banner tone="warning" title="Storefront sync">
            <p>{syncWarning}</p>
          </Banner>
        ) : null}

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingSm">
              Theme cart integration
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Dawn, Horizon, Craft, Sense, and other Online Store 2.0 themes work automatically. If
              upsell add-to-cart does not open or refresh the cart drawer, add your theme&apos;s CSS
              selectors below (comma-separated for multiple options).
            </Text>
            <TextField
              label="Cart drawer element"
              value={theme.cart_drawer_selector}
              onChange={(v) => patchTheme({ cart_drawer_selector: v })}
              autoComplete="off"
              helpText="Example: cart-drawer, #CartDrawer, .cart-drawer"
            />
            <TextField
              label="Open cart drawer (click targets)"
              value={theme.cart_drawer_open_selectors}
              onChange={(v) => patchTheme({ cart_drawer_open_selectors: v })}
              autoComplete="off"
              helpText="Example: [data-cart-drawer-toggle], #cart-icon-bubble, .header__icon--cart"
            />
            <TextField
              label="Cart section IDs"
              value={theme.cart_section_ids}
              onChange={(v) => patchTheme({ cart_section_ids: v })}
              autoComplete="off"
              helpText="Extra Shopify section IDs for cart refresh. Example: cart-drawer, cart-icon-bubble, cart-notification"
            />
            <TextField
              label="Cart drawer inner container"
              value={theme.cart_drawer_inner_selector}
              onChange={(v) => patchTheme({ cart_drawer_inner_selector: v })}
              autoComplete="off"
              helpText="Where cart HTML is injected if your theme does not use standard section IDs."
            />
            <TextField
              label="Cart count bubble"
              value={theme.cart_bubble_selector}
              onChange={(v) => patchTheme({ cart_bubble_selector: v })}
              autoComplete="off"
              helpText="Optional — selector for item count badge (e.g. .cart-count-bubble)."
            />
            <Checkbox
              label="Open cart drawer after upsell add to cart"
              checked={theme.open_drawer_after_add !== false}
              onChange={(v) => patchTheme({ open_drawer_after_add: v })}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingSm">
              Developers
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Custom CSS and JavaScript apply to every Free Shipping Bar widget on your storefront.
            </Text>
            <TextField
              label="Custom CSS"
              value={customCss}
              onChange={setCustomCss}
              multiline={8}
              autoComplete="off"
            />
            <TextField
              label="Custom JavaScript"
              value={customJs}
              onChange={setCustomJs}
              multiline={8}
              autoComplete="off"
              helpText="Runs once per page when the shipping bar loads. Use carefully on live stores."
            />
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
